import { useCallback, useEffect, useState } from 'react';

import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import type { LWApiPublicBillingStripePaymentBeginPaymentParamsRaw } from '@liquidweb-dev/club-sauce/public/billing/stripe/payment/index.raw';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import toast from '@liquidweb-dev/undercurrent/toast';

import FadeCollapse from 'components/FadeCollapse';
import { AcceptDispatchOkI } from 'modules/acceptJs';
import useBillingStripeBeginPayment from 'modules/queries/billing/stripe/payment/useBeginPayment';
import {
	ConfirmSavedPaymentContent,
	ConfirmNewAuthNetPaymentContent,
	ConfirmNewStripePaymentContent,
	StartContent,
} from './contents';
import dialogMakePaymentContext, {
	type DialogMakePaymentFormErrors,
	type DialogMakePaymentFormValues,
} from './dialogMakePaymentContext';
import StripeProvider from './StripeProvider';
import titles, { type DialogMakePaymentStep } from './titles';
import { type ResolvedPaymentMethodI } from './usePaymentMethods';

const initialFormValues: DialogMakePaymentFormValues = {
	amount: '0',
	payFullBalance: false,
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
	savePaymentMethod: false,
	paymentMode: 'saved',
	paymentInstrumentId: '',
};

const initialFormErrors: DialogMakePaymentFormErrors = {
	amount: '',
	payFullBalance: '',
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
	savePaymentMethod: '',
	paymentMode: '',
	paymentInstrumentId: '',
};

/** The values the form opens with, and the ones it returns to when it closes */
const getInitialFormValues = ({
	accountBalanceDisplay,
	defaultPaymentInstrumentId,
	initialPaymentMode,
}: {
	accountBalanceDisplay: string;
	defaultPaymentInstrumentId: string;
	initialPaymentMode: DialogMakePaymentFormValues['paymentMode'];
}): DialogMakePaymentFormValues => ({
	...initialFormValues,
	amount: accountBalanceDisplay,
	/** should be true if there is an account balance */
	payFullBalance: Number(accountBalanceDisplay) > 0,
	paymentMode: initialPaymentMode,
	paymentInstrumentId: defaultPaymentInstrumentId,
});

export interface PaymentStepsProps {
	accountBalanceDisplay: string;
	defaultPaymentInstrumentId: string;
	hasPaymentMethodsError: boolean;
	isPayPalAllowed: boolean;
	isStripeAllowed: boolean;
	/** whether the dialog holding these steps is open */
	open: boolean;
	paymentMethods: ResolvedPaymentMethodI[];
	onClose: () => void;
	refetchPaymentMethods: () => void;
}

/**
 * The steps of the payment, and the form state they share. Mounted only once the
 * account data has arrived, so every value it opens with is ready to be an
 * initial value - nothing to initialize later, and nothing to rearrange under
 * the user.
 */
const PaymentSteps = ({
	accountBalanceDisplay,
	defaultPaymentInstrumentId,
	hasPaymentMethodsError,
	isPayPalAllowed,
	isStripeAllowed,
	open,
	paymentMethods,
	onClose,
	refetchPaymentMethods,
}: PaymentStepsProps) => {
	// an account with nothing on file to pay with starts on a new card - a failure
	// to fetch is not an empty wallet, and has its own say on the saved panel
	const initialPaymentMode =
		paymentMethods.length > 0 || hasPaymentMethodsError ? 'saved' : 'new';

	const [step, setStep] = useState<DialogMakePaymentStep>('start');
	const [opaqueData, setOpaqueData] = useState<
		AcceptDispatchOkI['opaqueData'] | null
	>(null);
	const [formValues, setFormValues] = useState<DialogMakePaymentFormValues>(
		() =>
			getInitialFormValues({
				accountBalanceDisplay,
				defaultPaymentInstrumentId,
				initialPaymentMode,
			}),
	);
	const [formErrors, setFormErrors] = useState(initialFormErrors);

	const {
		data: beginStripeData,
		mutateAsync: beginPayment,
		isLoading: isBeginningStripePayment,
		reset: resetBeginPayment,
	} = useBillingStripeBeginPayment();
	const [authorizedAmount, setAuthorizedAmount] = useState('');

	/** Puts the steps back to how the dialog opens. */
	const reset = useCallback(() => {
		setStep('start');
		setOpaqueData(null);
		setAuthorizedAmount('');
		setFormErrors(initialFormErrors);
		setFormValues(
			getInitialFormValues({
				accountBalanceDisplay,
				defaultPaymentInstrumentId,
				initialPaymentMode,
			}),
		);
		// the intent that was begun goes with it, so nothing captures a stale one
		resetBeginPayment();
	}, [
		accountBalanceDisplay,
		defaultPaymentInstrumentId,
		initialPaymentMode,
		resetBeginPayment,
	]);

	/** The one way out of the dialog, so no close of ours can skip the reset. */
	const handleClose = useCallback(() => {
		reset();
		onClose();
	}, [reset, onClose]);

	// The backstop for the closes that are not ours: escape and a backdrop click
	// are the `Dialog`'s own, wired above these steps, so they never reach
	// `handleClose`. Resetting again after one of ours costs nothing.
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	/**
	 * Begins a Stripe payment, holding on to the amount it authorized. A capture
	 * may not exceed what was authorized, so it is taken from here rather than
	 * read back off a form the user may since have changed.
	 */
	const beginStripePayment = useCallback(
		async (params: LWApiPublicBillingStripePaymentBeginPaymentParamsRaw) => {
			const beginPaymentData = await beginPayment(params);

			setAuthorizedAmount(String(params.amount));

			return beginPaymentData;
		},
		[beginPayment],
	);

	const clearErrors = () => setFormErrors(initialFormErrors);

	const clearSensitiveFormValues = useCallback(() => {
		setFormValues((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
		// also clear any errors associated with these form values
		setFormErrors((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
	}, []);

	const handleStepChange = (newStep: DialogMakePaymentStep) => {
		setStep(newStep);
		clearErrors();
	};

	const handleSubmitSuccess = () => {
		handleClose();
		toast.success('Payment successful', {
			description: `Your payment of ${formatCurrency(formValues.amount)} was confirmed`,
		});
	};

	/**
	 * An error during the final submit is deliberately vague since we are not certain if the payment was in fact captured during an error.
	 */
	const handleSubmitError = () => {
		toast.danger('We couldn’t complete your payment', {
			description:
				'Check your balance before trying again, and contact support if it keeps failing.',
		});
		setStep('start');
	};

	return (
		<>
			<DialogTitle onClose={handleClose}>{titles[step]}</DialogTitle>
			<DialogContent>
				<dialogMakePaymentContext.Provider
					value={{
						accountBalanceDisplay,
						authorizedAmount,
						formErrors,
						formValues,
						hasPaymentMethodsError,
						isBeginningStripePayment,
						isPayPalAllowed,
						isStripeAllowed,
						paymentIntentId: beginStripeData?.payment_intent_id,
						paymentMethods,
						opaqueData,
						step,
						beginStripePayment,
						clearSensitiveFormValues,
						onSubmitSuccess: handleSubmitSuccess,
						onSubmitError: handleSubmitError,
						refetchPaymentMethods,
						setFormErrors,
						setFormValues,
						setOpaqueData,
						setStep: handleStepChange,
					}}
				>
					<StripeProvider>
						{/* each step is unmounted while collapsed - a step that isn't on
						screen shouldn't still be holding form inputs or firing requests */}
						<FadeCollapse in={step === 'start'} unmountOnExit>
							<StartContent />
						</FadeCollapse>

						<FadeCollapse in={step === 'confirmSavedPayment'} unmountOnExit>
							<ConfirmSavedPaymentContent />
						</FadeCollapse>

						<FadeCollapse
							in={step === 'confirmNewAuthNetPayment'}
							unmountOnExit
						>
							<ConfirmNewAuthNetPaymentContent />
						</FadeCollapse>

						<FadeCollapse in={step === 'confirmNewStripePayment'} unmountOnExit>
							<ConfirmNewStripePaymentContent />
						</FadeCollapse>
					</StripeProvider>
				</dialogMakePaymentContext.Provider>
			</DialogContent>
		</>
	);
};

export default PaymentSteps;
